// @ts-nocheck
import { TextFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import { GridWidths } from "@insite/mobius/GridItem";
import {
    parseBreakpointString,
    validateBreakpointTextField,
} from "@toolkit/Framework/Common/Utilities/breakpointString";
import { useMemo } from "react";

export type NumberTextFieldValue = string | number | undefined | null;
export interface NumberTextFieldOptions {
    integer?: boolean;
    isChildDefinition?: boolean;
    max?: number;
    min?: number;
    trim?: boolean;
}

export const validateNumberTextField = (value: NumberTextFieldValue, options?: NumberTextFieldOptions) => {
    const { integer, max, min, trim } = options || {};
    let _value = typeof value === "number" ? `${value}` : value;

    if (trim) {
        _value = _value?.trim();
    }

    if (!_value) {
        return "";
    }

    const number = Number(_value);

    if (Number.isNaN(number)) {
        return `Value must be a number`;
    }

    if (integer && (!Number.isInteger(number) || _value.includes("."))) {
        return `Value must not contain decimals`;
    }

    const hasMinError = typeof min === "number" && number < min;
    const hasMaxError = typeof max === "number" && number > max;

    if (hasMinError && hasMaxError) {
        return `Value must be between ${min} and ${max}`;
    }

    if (hasMinError) {
        return `Value must be greater than ${min}`;
    }

    if (hasMaxError) {
        return `Value must be lower than ${max}`;
    }

    return "";
};

export const getNumberTextFieldData = (value: NumberTextFieldValue, options?: NumberTextFieldOptions) => {
    const { trim } = options || {};

    return parseBreakpointString(`${value}`, trim)?.map(parsedValue => {
        if (!parsedValue) {
            return null;
        }

        const number = Number(parsedValue);

        return !Number.isNaN(number) ? number : null;
    });
};

export const getNumberTextFieldDefinition = (name: string, options?: NumberTextFieldOptions) => {
    const { isChildDefinition, trim } = options || {};

    const definition: TextFieldDefinition = {
        name,
        fieldType: !isChildDefinition ? "General" : (undefined as any),
        editorTemplate: "TextField",
        defaultValue: "",
        validate: (value, item) => validateBreakpointTextField(value, x => validateNumberTextField(x, options), trim),
        tooltip: `A single or an array (breakpoints) of numbers.`,
    };

    return definition;
};

const getNumberTextField = (name: string, options?: NumberTextFieldOptions) => {
    const _options = { trim: true, ...options };

    const parse = (value: NumberTextFieldValue) => getNumberTextFieldData(value, _options);
    const parseToGridWidths = (value: NumberTextFieldValue) => {
        if (!value) {
            return undefined;
        }

        const data = getNumberTextFieldData(value, _options) as GridWidths[];

        if (!data) {
            return undefined;
        }

        const result: GridWidths[] = [];
        let width: GridWidths = 12;

        for (let i = 0; i < 5 /* breakpoints */; i++) {
            const currentWidth = data[i];
            if (typeof currentWidth === "number") {
                width = data[i];
            }

            result[i] = width;
        }

        return result;
    };

    return {
        definition: getNumberTextFieldDefinition(name, _options),
        parse,
        useParse: (...parameters: Parameters<typeof parse>) => {
            return useMemo(() => parse(...parameters), parameters);
        },
        parseToGridWidths,
        useParseToGridWidths: (...parameters: Parameters<typeof parseToGridWidths>) => {
            return useMemo(() => parseToGridWidths(...parameters), parameters);
        },
    };
};

export default getNumberTextField;
